<!-- lib/components/BackToTop.svelte -->
<script>
  export let displayThresholdPx = 600;
  export let onAfterScroll = null;

  let hidden = true;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    onAfterScroll?.();
  };

  const handleOnScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    hidden = scrollTop <= displayThresholdPx;
  };

  const handleKeydown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      scrollToTop();
    }
  };
</script>

<svelte:window on:scroll={handleOnScroll} />

<button
  class="back-to-top"
  class:hidden
  on:click={scrollToTop}
  on:keydown={handleKeydown}
  aria-label="Back to top"
  title="Back to top"
>
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 14L12 9L17 14"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</button>

<style lang="scss">
  .back-to-top {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    z-index: 1000;

    display: flex;
    align-items: center;
    justify-content: center;

    // Glassmorphism styling to match your theme
    background: rgba(210, 165, 82, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.3);
    box-shadow:
      0 8px 32px rgba(210, 165, 82, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.2);

    color: #1e0f30;

    // Smooth transitions
    transition: all 0.3s ease;
    opacity: 1;
    visibility: visible;
    transform: translateY(0);

    &:hover {
      background: rgba(210, 165, 82, 1);
      transform: translateY(-2px);
      box-shadow:
        0 12px 40px rgba(210, 165, 82, 0.4),
        0 4px 12px rgba(0, 0, 0, 0.3);
      border-color: rgba(255, 255, 255, 0.3);
    }

    &:active {
      transform: translateY(0);
      box-shadow:
        0 6px 24px rgba(210, 165, 82, 0.3),
        0 2px 6px rgba(0, 0, 0, 0.2);
    }

    &:focus {
      outline: 2px solid var(--text-color1, #d2a552);
      outline-offset: 3px;
    }

    &.hidden {
      opacity: 0;
      visibility: hidden;
      transform: translateY(20px);
      pointer-events: none;
    }

    // Icon styling
    svg {
      flex-shrink: 0;
      transition: transform 0.2s ease;
    }

    &:hover svg {
      transform: translateY(-1px);
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .back-to-top {
      bottom: 1.5rem;
      right: 1.5rem;
      width: 48px;
      height: 48px;

      svg {
        width: 20px;
        height: 20px;
      }
    }
  }

  @media (max-width: 480px) {
    .back-to-top {
      bottom: 1rem;
      right: 1rem;
      width: 44px;
      height: 44px;

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }

  @media (max-width: 320px) {
    .back-to-top {
      bottom: 0.75rem;
      right: 0.75rem;
      width: 40px;
      height: 40px;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  // Reduce motion for accessibility
  @media (prefers-reduced-motion: reduce) {
    .back-to-top {
      transition:
        opacity 0.3s ease,
        visibility 0.3s ease;

      &:hover,
      &:active {
        transform: none;
      }

      &.hidden {
        transform: none;
      }

      svg {
        transition: none;
      }

      &:hover svg {
        transform: none;
      }
    }
  }
</style>
